// Occasion engine — pure date-aware reasoning about "what's
// relevant right now."
//
// The DISCOVERY_TAGS table in src/data/recipe_stubs/tags.js
// declares which tags are seasonal (fixed or dynamic date) vs
// recurring (day-of-week + time-window) vs always-on. This
// module reads that table and answers:
//   activeOccasions(now) → which tags are "in season" RIGHT NOW
//                          and with what weight
//   nextOccurrence(tag)  → when does this tag next happen
//                          (for push notification scheduling)
//   isWithinLead(tag, now) → boolean: is `now` inside the tag's
//                            lead-window?
//
// Always-on tags (no seasonal/recurring definition) are NOT
// returned by activeOccasions — they're available for stubs to
// fly but only surface via direct intent (e.g., a user-driven
// "kid friendly" filter), not via this engine.

import { DISCOVERY_TAGS } from "../data/recipe_stubs/tags";

// ── Seasonal helpers ────────────────────────────────────────────

// Compute the next occurrence of a fixed-date or dynamic-date
// seasonal tag, returning a Date. If the date this year has
// already passed, returns next year's. Used both by activeOccasions
// (to compute daysOut) and by push notification scheduling.
function nextSeasonalDate(tag, now) {
  const s = tag.seasonal;
  if (!s) return null;
  const thisYear = now.getFullYear();
  const candidate = s.dynamicDate
    ? s.dynamicDate(thisYear)
    : new Date(thisYear, s.month, s.day);
  // If this year's date is already > leadDays in the PAST, roll
  // to next year. (Within the lead window or future = use this year.)
  const msPast = now.getTime() - candidate.getTime();
  if (msPast > 0 && msPast > 0) {
    // Past — return next year's occurrence
    return s.dynamicDate
      ? s.dynamicDate(thisYear + 1)
      : new Date(thisYear + 1, s.month, s.day);
  }
  return candidate;
}

// ── Recurring helpers ───────────────────────────────────────────

// `recurring.dow` can be an int or an array; normalize to an array.
function dowSet(recurring) {
  if (!recurring?.dow) return [];
  return Array.isArray(recurring.dow) ? recurring.dow : [recurring.dow];
}

// Time-window can wrap past midnight (e.g., late-night is
// [22, 28] meaning 10pm-4am). normalize check: hour is in
// [start, end mod 24] with wrap detection.
function inTimeWindow(recurring, now) {
  const w = recurring?.timeWindow;
  if (!Array.isArray(w) || w.length !== 2) return true; // no window = always
  const [start, end] = w;
  const hour = now.getHours();
  if (end <= 24) {
    return hour >= start && hour < end;
  }
  // Wraps past midnight (e.g., [22, 28] = 10pm-4am): start AND wrapped end
  const wrappedEnd = end - 24;
  return hour >= start || hour < wrappedEnd;
}

// ── Public API ──────────────────────────────────────────────────

// Returns the discovery tags that are "in season" at `now`,
// sorted by weight descending. Each entry:
//   {
//     tag:     "july4th"
//     def:     { label, emoji, seasonal | recurring, ... }
//     kind:    "seasonal" | "recurring"
//     daysOut: int    // 0 = today (seasonal only), null for recurring
//     weight:  number // larger = more relevant
//   }
//
// Weight model:
//   - Seasonal tags get a base weight (100) discounted by daysOut /
//     leadDays so a 14-day-out occasion sits below a 1-day-out one.
//   - Recurring tags get a flat (50) when their window matches.
//   - Tags both inside their seasonal lead AND matching their
//     recurring window stack (unusual but supported — e.g., a
//     "thanksgiving-brunch" tag).
export function activeOccasions(now = new Date()) {
  const out = [];
  for (const [tag, def] of Object.entries(DISCOVERY_TAGS)) {
    if (def.seasonal) {
      const date = nextSeasonalDate(def, now);
      if (!date) continue;
      const daysOut = Math.floor((date.getTime() - now.getTime()) / 86400000);
      const lead = def.seasonal.leadDays ?? 0;
      // Inside the lead window? (daysOut between 0 and leadDays)
      if (daysOut >= 0 && daysOut <= lead) {
        // Weight: 100 when daysOut=0, scaling down to ~10 at leadDays.
        const weight = 100 - (90 * daysOut) / Math.max(1, lead);
        out.push({ tag, def, kind: "seasonal", daysOut, weight });
      }
    }
    if (def.recurring) {
      const dows = dowSet(def.recurring);
      const dow = now.getDay();
      if (dows.includes(dow) && inTimeWindow(def.recurring, now)) {
        // Recurring is steady mid-weight — beaten by close seasonal
        // occasions but beats far-off ones.
        const existing = out.find((o) => o.tag === tag);
        if (existing) {
          // Both seasonal AND recurring fired (e.g., a Sunday in
          // Christmas week). Stack the weight.
          existing.weight += 30;
          existing.kind = "seasonal+recurring";
        } else {
          out.push({ tag, def, kind: "recurring", daysOut: null, weight: 50 });
        }
      }
    }
  }
  out.sort((a, b) => b.weight - a.weight);
  return out;
}

// True iff the tag's seasonal lead window is currently open. Used
// by push scheduling so we don't push "Easter is coming!" 2 months
// out.
export function isWithinLead(tagId, now = new Date()) {
  const def = DISCOVERY_TAGS[tagId];
  if (!def?.seasonal) return false;
  const date = nextSeasonalDate(def, now);
  if (!date) return false;
  const daysOut = Math.floor((date.getTime() - now.getTime()) / 86400000);
  const lead = def.seasonal.leadDays ?? 0;
  return daysOut >= 0 && daysOut <= lead;
}

// When does this tag NEXT happen, as a Date? Seasonal tags return
// the date itself; recurring tags return the next matching dow +
// time-window start. Used by push notification scheduling +
// recommendation copy ("3 days until July 4th").
export function nextOccurrence(tagId, now = new Date()) {
  const def = DISCOVERY_TAGS[tagId];
  if (!def) return null;
  if (def.seasonal) {
    return nextSeasonalDate(def, now);
  }
  if (def.recurring) {
    const dows = dowSet(def.recurring);
    if (dows.length === 0) return null;
    const window = def.recurring.timeWindow || [0, 24];
    const startHour = window[0];
    // Find the next dow + hour that matches.
    for (let offset = 0; offset < 8; offset++) {
      const candidate = new Date(now);
      candidate.setDate(candidate.getDate() + offset);
      candidate.setHours(startHour, 0, 0, 0);
      if (!dows.includes(candidate.getDay())) continue;
      if (candidate.getTime() <= now.getTime()) continue;
      return candidate;
    }
    return null;
  }
  return null;
}
